import i18n from "@plugins/i18n";
import { ALPHABETS_LIST } from "@modules/editor/service/editor.constant";
import type { FillInBlankOptionDraft } from "@modules/editor/service/fill-in-blank.utils";

const GROUP_COLOR_PALETTE = [
  "#1D979F",
  "#224FAE",
  "#8C5EA8",
  "#5A6066",
  "#C26726",
  "#B68506",
  "#C16565",
];

const GROUP_SEPARATOR_HTML = "<p><br></p>";

export interface FillInBlankGroupDisplayRow {
  id: string;
  option: FillInBlankOptionDraft;
  optionIndexes: number[];
  blankNumbers: number[];
  optionGroupNo: number | null;
  isGrouped: boolean;
  color: string | null;
  label: string;
}

const cloneOption = (option: FillInBlankOptionDraft): FillInBlankOptionDraft => ({ ...option });

const getGroupedPayload = (
  options: FillInBlankOptionDraft[],
  overrides: Partial<FillInBlankOptionDraft> = {},
): FillInBlankOptionDraft => {
  const firstOption = options[0] ? cloneOption(options[0]) : {};
  const answers = [
    ...new Set(
      options.flatMap((option) =>
        (option.answer ?? "")
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    ),
  ].join("|");
  const distractors = [
    ...new Set(
      options.flatMap((option) =>
        (option.wrongAnswer ?? "")
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    ),
  ].join("|");
  const feedback = [
    ...new Set(options.map((option) => (option.feedback ?? "").trim()).filter(Boolean)),
  ].join(GROUP_SEPARATOR_HTML);
  const distractorFeedback = [
    ...new Set(options.map((option) => (option.wrongAnswerFeedback ?? "").trim()).filter(Boolean)),
  ].join(GROUP_SEPARATOR_HTML);

  return {
    ...firstOption,
    answer: answers,
    caseSensitive: firstOption.caseSensitive ?? false,
    feedback,
    wrongAnswer: distractors,
    wrongAnswerFeedback: distractorFeedback,
    ...overrides,
  };
};

export const groupBlanks = (
  options: FillInBlankOptionDraft[],
  blankIndexes: number[],
  groupNo: number,
): FillInBlankOptionDraft[] => {
  const selectedIndexes = [...new Set(blankIndexes)].sort((left, right) => left - right);

  if (selectedIndexes.length < 2) {
    return options.map(cloneOption);
  }

  const selectedOptions = selectedIndexes
    .map((index) => options[index])
    .filter((option): option is FillInBlankOptionDraft => option != null);

  if (selectedOptions.length < 2) {
    return options.map(cloneOption);
  }

  const groupCaseSensitive = selectedOptions.every((option) => option.caseSensitive === true);
  const groupedPayload = getGroupedPayload(selectedOptions, { caseSensitive: groupCaseSensitive });

  return options.map((option, index) => {
    if (!selectedIndexes.includes(index)) {
      return cloneOption(option);
    }

    return {
      ...cloneOption(option),
      answer: groupedPayload.answer ?? "",
      caseSensitive: groupCaseSensitive,
      feedback: groupedPayload.feedback ?? "",
      optionGroupNo: groupNo,
      wrongAnswer: groupedPayload.wrongAnswer ?? "",
      wrongAnswerFeedback: groupedPayload.wrongAnswerFeedback ?? "",
    };
  });
};

export const ungroupBlanks = (
  options: FillInBlankOptionDraft[],
  groupNo: number,
): FillInBlankOptionDraft[] => {
  const groupedOptions = options.filter((option) => option.optionGroupNo === groupNo);

  if (groupedOptions.length < 1) {
    return options.map(cloneOption);
  }

  const groupedPayload = cloneOption(groupedOptions[0]);

  return options.map((option) => {
    if (option.optionGroupNo !== groupNo) {
      return cloneOption(option);
    }

    return {
      ...cloneOption(option),
      answer: groupedPayload.answer ?? "",
      caseSensitive: groupedPayload.caseSensitive ?? false,
      feedback: groupedPayload.feedback ?? "",
      optionGroupNo: null,
      wrongAnswer: groupedPayload.wrongAnswer ?? "",
      wrongAnswerFeedback: groupedPayload.wrongAnswerFeedback ?? "",
    };
  });
};

export const cleanupSingletonGroups = (
  options: FillInBlankOptionDraft[],
): FillInBlankOptionDraft[] => {
  const groupedIndexMap = new Map<number, number[]>();

  options.forEach((option, index) => {
    if (option.optionGroupNo == null) {
      return;
    }

    const indexes = groupedIndexMap.get(option.optionGroupNo) ?? [];
    indexes.push(index);
    groupedIndexMap.set(option.optionGroupNo, indexes);
  });

  return options.map((option) => {
    if (option.optionGroupNo == null) {
      return cloneOption(option);
    }

    const memberIndexes = groupedIndexMap.get(option.optionGroupNo) ?? [];

    if (memberIndexes.length > 1) {
      return cloneOption(option);
    }

    return {
      ...cloneOption(option),
      optionGroupNo: null,
    };
  });
};

export const reindexGroups = (options: FillInBlankOptionDraft[]): FillInBlankOptionDraft[] => {
  const groupOrder = new Map<number, number>();
  let nextGroupNo = 1;

  options.forEach((option) => {
    if (option.optionGroupNo == null || groupOrder.has(option.optionGroupNo)) {
      return;
    }

    groupOrder.set(option.optionGroupNo, nextGroupNo);
    nextGroupNo += 1;
  });

  return options.map((option) => {
    if (option.optionGroupNo == null) {
      return cloneOption(option);
    }

    return {
      ...cloneOption(option),
      optionGroupNo: groupOrder.get(option.optionGroupNo) ?? option.optionGroupNo,
    };
  });
};

export const getGroupColor = (groupNo: number): string => {
  const normalizedGroupNo = Math.max(groupNo, 1);
  return GROUP_COLOR_PALETTE[(normalizedGroupNo - 1) % GROUP_COLOR_PALETTE.length];
};

export const getGroupLabel = (groupNo: number): string => {
  const letter = ALPHABETS_LIST[groupNo - 1] ?? String(groupNo);
  return i18n.t("question_component_fitb_group_label", { letter });
};

export const getGroupDisplayRows = (
  options: FillInBlankOptionDraft[],
): FillInBlankGroupDisplayRow[] => {
  const groupedRows = new Map<number, FillInBlankGroupDisplayRow>();
  const rows: FillInBlankGroupDisplayRow[] = [];

  options.forEach((option, index) => {
    const blankNumber = index + 1;
    const optionGroupNo = option.optionGroupNo ?? null;

    if (optionGroupNo == null) {
      rows.push({
        id: `blank-${option.id}`,
        option: cloneOption(option),
        optionIndexes: [index],
        blankNumbers: [blankNumber],
        optionGroupNo: null,
        isGrouped: false,
        color: null,
        label: `Answer ${blankNumber}`,
      });
      return;
    }

    const existingRow = groupedRows.get(optionGroupNo);

    if (!existingRow) {
      const nextRow: FillInBlankGroupDisplayRow = {
        id: `group-${option.id}`,
        option: cloneOption(option),
        optionIndexes: [index],
        blankNumbers: [blankNumber],
        optionGroupNo,
        isGrouped: true,
        color: getGroupColor(optionGroupNo),
        label: getGroupLabel(optionGroupNo),
      };

      groupedRows.set(optionGroupNo, nextRow);
      rows.push(nextRow);
      return;
    }

    existingRow.optionIndexes.push(index);
    existingRow.blankNumbers.push(blankNumber);
    existingRow.option = getGroupedPayload(
      existingRow.optionIndexes.map((optionIndex) => options[optionIndex]),
    );
  });

  return rows;
};
